import _get from 'lodash/get';
import _isObject from 'lodash/isObject';

import { RB_THEME } from 'components/resumeBuilder/template/themes/info';
import { HttpRoutes, JOB_BUCKET_GROUP } from 'utils/constant';

import {
  addUserGroupToData,
  filterOnlyUSLocations,
  format,
  getStateName,
  removeSpecialCharacter,
} from '../utils';

import { logActivity, logDLPJobActivity } from './logActivity';
import Service from './service';

const handleJobActions = async ({ url, payload }) => {
  try {
    const response = await Service.post(url, payload);
    if (response && response.data === 'SUCCESS') {
      return true;
    }
    throw new Error('Error');
  } catch (e) {
    return e;
  }
};

/**
 * @returns {Promise<{
 *  jobs,
 *  totalJobs
 * }| Error>}
 */
const getCompanyJobs = async (payload = {}) => {
  try {
    const newPayload = filterOnlyUSLocations(payload);
    const response = await Service.post(HttpRoutes.companyJobs, {
      ...newPayload,
      numJobs: newPayload.numJobs || 20,
    });
    const { data } = response;
    if (data) {
      return data;
    }
    throw new Error('Error');
  } catch (e) {
    return e;
  }
};

/**
 * @returns {Promise<{
 *  listings,
 *  totalJobs
 * }}
 */
const getUserJobs = async (payload = {}) => {
  try {
    const newPayload = filterOnlyUSLocations(payload);
    const response = await Service.post(HttpRoutes.bucketsJobs, {
      ...newPayload,
      jobsPerPage: newPayload.numJobs || 20,
    });
    const { data } = response;
    if (data) {
      return data;
    }
    throw new Error('Error');
  } catch (e) {
    return e;
  }
};

const getUserSavedJobs = async (payload = {}) => {
  try {
    const response = await Service.post(HttpRoutes.userSavedJobs, payload);
    const { data } = response;
    return data;
  } catch (e) {
    return e;
  }
};

const getUserAppliedJobs = async (payload = {}) => {
  try {
    const { data } = await Service.post(HttpRoutes.userAppliedJobs, payload);
    return data;
  } catch (e) {
    return e;
  }
};

const getTitleJobs = async (payload = {}) => {
  const newPayload = filterOnlyUSLocations(payload);
  if (newPayload?.locations?.length) {
    // Filter locations which have either state or stateCode property
    Object.assign(newPayload, {
      locations:
        newPayload?.locations?.filter((location) => location.state || location.stateCode) || [],
    });
  }
  const response = await Service.post(HttpRoutes.jobPostings, {
    ...newPayload,
    numJobs: newPayload.numJobs || 20,
  });
  const { data } = response;
  if (data) {
    return data;
  }
  throw new Error('Error');
};

const getHighSchoolJobs = async (payload = {}) => {
  try {
    const newPayload = filterOnlyUSLocations(payload);
    const payloadParsed = {};
    Object.keys(newPayload).forEach((key) => {
      if (newPayload[key] && !!newPayload[key] && newPayload[key].length) {
        payloadParsed[key] = newPayload[key];
      }
    });
    payloadParsed.numJobs = newPayload.numJobs || 20;
    payloadParsed.remote = newPayload.remote || false;
    payloadParsed.fetchJobDesc = true;
    const response = await Service.post(HttpRoutes.highSchoolJobs, payloadParsed);
    const { data } = response;
    if (data) {
      return data;
    }
    throw new Error('Error');
  } catch (e) {
    return e;
  }
};

const getRemoteJobs = async (payload = {}) => {
  try {
    const newPayload = filterOnlyUSLocations(payload);
    const payloadParsed = {};
    Object.keys(newPayload).forEach((key) => {
      if (
        newPayload[key] === true ||
        (newPayload[key] && !!newPayload[key] && newPayload[key].length)
      ) {
        payloadParsed[key] = newPayload[key];
      }
    });
    payloadParsed.numJobs = newPayload.numJobs || 20;
    if (newPayload.salary) {
      payloadParsed.salary = newPayload.salary;
    }
    const response = await Service.post(HttpRoutes.remoteJobs, payloadParsed);
    const { data } = response;
    if (data) {
      return data;
    }
    throw new Error('Error');
  } catch (e) {
    return e;
  }
};

const getJobsBySkill = async (payload = {}) => {
  try {
    const criteria = payload;
    //  * city
    criteria.city = criteria.city || _get(criteria, 'locations[0].city', '');
    //  * state
    criteria.state = criteria.state || getStateName(_get(criteria, 'locations[0].state', ''));
    //  * title
    criteria.title = criteria.title || '';
    //  * skill
    criteria.skill = criteria.skill ? encodeURIComponent(criteria.skill) : '';
    //  * socCode
    criteria.socCode = criteria.socCode || '';
    //  * start
    criteria.start = criteria.previousListingHashes.length;
    //  * numJobs
    criteria.numJobs = criteria.numJobs || 20;
    //  * locationSort
    criteria.locationSort = !criteria.city && !criteria.state ? true : false;
    const response = await Service.get(format(HttpRoutes.jobsBySkill, criteria));
    if (response && response.data) {
      return response.data;
    }
    throw new Error('Error while getting getJobsBySkill');
  } catch (e) {
    return { jobs: [], totalJobs: 0 };
  }
};

const getMostSimilarJobs = async (payload = {}) => {
  try {
    const newPayload = filterOnlyUSLocations(payload);
    const response = await Service.post(HttpRoutes.mostSimilarJobs, newPayload);
    if (response && response.data) {
      return response.data;
    }
    throw new Error('Error while getting getMostSimilarJobs');
  } catch (error) {
    return { jobs: [], totalJobs: 0 };
  }
};

/**
 * @param {Object} payload
 * @param {Object} payload.job
 * @param {String} payload.type
 * @param {Boolean} payload.favorite
 */
const toggleSave = (payload) =>
  handleJobActions({
    url: HttpRoutes.toggleSave,
    payload,
  });

/**
 * @param {Object} payload
 * @param {Object} payload.job
 * @param {String} payload.type
 * @param {Boolean} payload.dismissed
 */
const toggleDismiss = (payload) =>
  handleJobActions({
    url: HttpRoutes.toggleDismiss,
    payload,
  });

/**
 * @param {Object} param0.job job detail information
 */
const toggleApply = async ({ job, enableSignUpBeforeApply }) => {
  try {
    const payload = {
      job: {
        jobId: job.jobId,
        source: job.source,
        listingHash: job.listingHash,
        cpc: job.cpc,
        country: job.OBJcountry,
        city: job.OBJcity,
        state: job.OBJstate,
        stateCode: job.OBJstateCode,
        companyID: job.OBJcompanyID,
        formattedOriginalCompanyName: job.OBJcompanyDisplay,
        industry: job.OBJindustry,
        postingDate: job.OBJpostingDate,
        title: job.OBJtitle,
        titleDisplay: job.OBJtitleDisplay,
        url: job.OBJurl,
        zipcode: job.OBJzipcode,
        jobTags: job.OBJjobTags,
        socCode: job.socode,
        easyApply: !!job.easyApplyFlag,
        jobDescription: job.jobDescription,
      },
    };
    await handleJobActions({
      url: HttpRoutes.toggleApply,
      payload,
    });
    if (!enableSignUpBeforeApply) {
      logDLPJobActivity(job, 'JOB_OPENING_JOB_CLICKED_DLP');
    }
    return true;
  } catch (e) {
    return e;
  }
};

/* add job as seen
 * @param {Object} payload default payload
 * @param {Object} payload.job
 * @param {String} payload.type
 * @param {Boolean} payload.dismissed
 */
const addSeenJob = (payload) =>
  handleJobActions({
    url: HttpRoutes.addSeenJob,
    payload,
  });

const getUserlistinghashes = async () => {
  try {
    const listing = await Service.get(HttpRoutes.userListingHashes);
    if (listing && listing.data) {
      return listing.data;
    }
    throw new Error('Error in getUserlistinghashes:');
  } catch (e) {
    return e;
  }
};

/**
 * @param {Object} jobData job detail
 * @param {String} activityType JOB_OPENING_SAVED_CARD or JOB_OPENING_DISMISSED_CARD
 * @param {String} bucketName job detail container title
 */
const logNetflixJobActivity = (jobData, activityType, bucketName) => {
  if (!jobData) {
    return;
  }

  const payload = {
    bucketName,
    jobId: jobData.jobId,
    jobTitle: jobData.jobTitle,
    companyName: jobData.companyName,
    partnerName: jobData.source,
    searchTitle: jobData.titleID,
    url: window.location.href,
    jobUrl: jobData.url || jobData.OBJurl || jobData.jobUrl,
    cpc: jobData.cpc,
    originalCPC: jobData.originalCPC,
    listingHash: jobData.listingHash,
  };
  addUserGroupToData(payload);
  logActivity({
    activityType,
    ...payload,
  });
};

const fetchJobsByHash = async (hash) => {
  try {
    const jobDetail = await Service.get(format(HttpRoutes.fetchJobsByHash, { hash }));
    if (jobDetail && jobDetail.data && jobDetail.data.totalJobs === 1) {
      return jobDetail.data.jobs[0];
    }
    throw new Error('Error');
  } catch (e) {
    return e;
  }
};

/**
 *
 * @param {Object} payload
 * @param {String} payload.listingHash
 * @param {String} payload.companyId
 */
const getJobTags = async (payload) => {
  try {
    const jobTags = await Service.get(format(HttpRoutes.jobTags, payload));
    if (jobTags && jobTags.data) {
      return jobTags.data.tags || [];
    }
    throw new Error('error');
  } catch (e) {
    return e;
  }
};

const getUserProperties = async () => {
  try {
    const userProperties = await Service.get(HttpRoutes.userProperties);
    if (userProperties && userProperties.data) {
      return userProperties.data;
    }
    throw new Error('error');
  } catch (e) {
    return e;
  }
};

const getRelatedJobs = async (payload = {}) => {
  try {
    const newPayload = filterOnlyUSLocations(payload);
    const response = await Service.post(HttpRoutes.relatedJobPostings, newPayload);
    if (response && response.data) {
      return response.data;
    }

    throw new Error('Error in jobService.getRelatedJobs');
  } catch (e) {
    return Promise.reject(e);
  }
};

const getMajorJobs = async (payload = {}) => {
  try {
    const newPayload = filterOnlyUSLocations(payload);
    const response = await Service.post(HttpRoutes.majorJobs, newPayload);
    if (response && response.data) {
      return response.data;
    }

    throw new Error('Error in jobService.getRelatedJobs');
  } catch (e) {
    return Promise.reject(e);
  }
};

const getPaidJobs = async (payload = {}) => {
  try {
    const newPayload = filterOnlyUSLocations(payload);
    if (payload.locations?.length && !newPayload.locations?.length) {
      return Promise.resolve({
        jobs: [],
        totalJobs: 0,
        remainingJobs: 0,
      });
    }
    const response = await Service.post(HttpRoutes.paidJobs, newPayload);
    if (response && response.data) {
      return response.data;
    } else {
      return [];
    }
  } catch (e) {
    return Promise.reject(e);
  }
};

const getJobsFromSearchAPI = async (payload = {}) => {
  try {
    const newPayload = filterOnlyUSLocations(payload);
    if (payload?.companyID && payload.companyID.length) {
      newPayload.companyIDs = payload.companyID;
      delete newPayload.companyID;
    }
    const response = await Service.post(HttpRoutes.getJobsFromSearchAPI, newPayload);
    if (response && response.data) {
      return response.data;
    } else {
      return [];
    }
  } catch (e) {
    return Promise.reject(e);
  }
};

const getActivityRecommendationsExtendedJobs = async (payload) => {
  try {
    const response = await Service.post(HttpRoutes.activityRecommendationsExtendedJobs, payload);
    if (response && response.data) {
      return response.data;
    }

    throw new Error('Error in jobService.getRelatedJobs');
  } catch (e) {
    return Promise.reject(e);
  }
};

const getEstimateSalaryForSalaryFilter = async (payload) => {
  try {
    const response = await Service.post(HttpRoutes.getEstimateSalaryForSalaryFilter, payload);

    if (response && response.data) {
      return response.data;
    }

    throw new Error('Error in jobService.getEstimateSalaryForSalaryFilter');
  } catch (e) {
    return Promise.reject(e);
  }
};

const getSalariesByJobTitleBatch = async (payload) => {
  try {
    const response = await Service.post(HttpRoutes.salaryByJobTitleBatch, payload);

    if (response && response.data) {
      return response.data;
    }

    throw new Error('Error in jobService.getSalariesByJobTitleBatch');
  } catch (e) {
    return Promise.reject(e);
  }
};

const downloadResumePdf = async ({ templateName, jobTitle = '' }) => {
  try {
    const templateIndex = Object.values(RB_THEME).indexOf(templateName);
    const modifiedTitle = removeSpecialCharacter(jobTitle.toLowerCase());
    const filename = `${modifiedTitle}-resume-${templateIndex + 1}.pdf`;
    const response = await Service.post(
      HttpRoutes.downloadResumePdf,
      { filename },
      {
        responseType: 'arraybuffer',
        headers: {
          Accept: 'application/pdf',
        },
      }
    );
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    return link.click();
  } catch (error) {
    return console.error({ msg: 'Error occurred in ResumeBuilderService.htmlToBlobDocx', error });
  }
};

const hasSponsoredJobs = async (payload) => {
  try {
    const response = await Service.post(HttpRoutes.hasSponsoredJobs, payload);
    if (response && response.data !== undefined) {
      return response.data;
    } else {
      return false;
    }
  } catch (e) {
    return Promise.reject(e);
  }
};

const fetchSponsoredJobs = async (payload) => {
  try {
    const response = await Service.post(HttpRoutes.fetchSponsoredJobs, payload);
    if (response && response.data) {
      return response.data;
    } else {
      return false;
    }
  } catch (e) {
    return Promise.reject(e);
  }
};

const fetchPersonalizedMatches = async (payload) => {
  try {
    const response = await Service.post(HttpRoutes.fetchPersonalizedMatches, payload);
    if (response && response.data) {
      return response.data;
    } else {
      return false;
    }
  } catch (e) {
    return Promise.reject(e);
  }
};

const getInitialJobsState = (data = {}, initialState = {}) => {
  const jobFieldName = Object.keys(data).find(
    (key) => _isObject(data[key]) && 'jobs' in data[key] && 'totalJobs' in data[key]
  );
  const jobGroup = (() => {
    if (data.jobGroup) return data.jobGroup;
    else return JOB_BUCKET_GROUP.JOBS_FROM_SEACH_API;
  })();
  return {
    ...initialState,
    ...(jobGroup ? { [jobGroup]: data[jobFieldName] || {} } : {}),
  };
};

const getJobsCountByTitle = async (titles = []) => {
  try {
    return await Service.post(HttpRoutes.getJobsCountByTitle, { titles });
  } catch (e) {
    return Promise.reject(e);
  }
};

export const jobService = {
  getCompanyJobs,
  getUserJobs,
  getUserSavedJobs,
  getTitleJobs,
  getJobsBySkill,
  getMajorJobs,
  toggleSave,
  toggleDismiss,
  toggleApply,
  addSeenJob,
  logNetflixJobActivity,
  getUserlistinghashes,
  getUserProperties,
  fetchJobsByHash,
  getJobTags,
  getRemoteJobs,
  getRelatedJobs,
  getHighSchoolJobs,
  getEstimateSalaryForSalaryFilter,
  getActivityRecommendationsExtendedJobs,
  getMostSimilarJobs,
  getPaidJobs,
  getUserAppliedJobs,
  getJobsFromSearchAPI,
  downloadResumePdf,
  hasSponsoredJobs,
  fetchSponsoredJobs,
  fetchPersonalizedMatches,
  getSalariesByJobTitleBatch,
  getInitialJobsState,
  getJobsCountByTitle,
};
